/**
 * 企業公司資訊型別與欄位分組（pm_41 Card B Task 0；2026-09-04 spec 批 E 擴節）。
 * 欄位對齊 server EnterpriseCompanyVm（enterprise-company.vm.ts）。
 */
export interface EnterpriseCompany {
	name?: string;
	uniform_number?: string | null;
	status?: number;
	logo_url?: string | null;
	description?: string | null;
	products_services?: string | null;
	latest_news?: string | null;
	custom_welfare?: string | null;
	capital_amount?: number | null;
	capital_show_status?: number | null;
	phone_code?: string | null;
	phone_number?: string | null;
	website?: string | null;
	area_code?: string | null;
	address?: string | null;
	industry_category_code?: string | null;
	employee_count_range_code?: string | null;
	/** 負責人／設立日期／董監事 + 三個顯示開關（spec §2.1、§3.1；`is_uniform_number_visible` 明確不開放，見 FORBIDDEN_FIELDS）。 */
	representative_name?: string | null;
	establishment_date?: string | null;
	directors?: string[] | null;
	is_representative_visible?: number;
	is_establishment_date_visible?: number;
	is_directors_visible?: number;
	/** 五個可整段取代區塊（讀端點才有；`company replace` 用其中四個，`video_links` 由 `company update` 的 descriptions 節寫入）。 */
	pain_points?: Array<Record<string, unknown>>;
	awards?: Array<Record<string, unknown>>;
	milestones?: Array<Record<string, unknown>>;
	qa_items?: Array<Record<string, unknown>>;
	video_links?: Array<Record<string, unknown>>;
	/** 五個區塊各自的 opaque revision（`If-Match` 用；不可解析內容，spec §3.1.1）。 */
	block_revisions?: Record<string, string>;
	[k: string]: unknown;
}

/**
 * `PATCH /company/basic` 欄位（6 必填 + 10 選填，`uniform_number` 唯讀不在此列）。
 * 2026-09-04 spec 批 E 擴節：負責人／設立日期／董監事 + 三個顯示開關（不含 `is_uniform_number_visible`，
 * 見 FORBIDDEN_FIELDS 與 spec §1「明確不做」）。
 */
export const BASIC_FIELDS: ReadonlyArray<string> = [
	'name',
	'industry_category_code',
	'phone_code',
	'phone_number',
	'employee_count_range_code',
	'capital_amount',
	'capital_show_status',
	'website',
	'area_code',
	'address',
	'representative_name',
	'establishment_date',
	'directors',
	'is_representative_visible',
	'is_establishment_date_visible',
	'is_directors_visible',
];

/**
 * `PATCH /company/descriptions` 欄位（4 個各自獨立選填，非 atomic）。
 * 2026-09-04 spec 批 E 擴節：新增 `video_links`——與其餘三個不同，`video_links` 若出現在輸入中
 * 需要額外的樂觀鎖（`If-Match`）與刪除確認流程，見 `update.ts` 對 `video_links` 的專門處理，
 * 不能只靠這份清單機械帶過。
 */
export const DESCRIPTION_FIELDS: ReadonlyArray<string> = ['description', 'products_services', 'latest_news', 'video_links'];

/**
 * 本地擋 CLI 輸入用的禁止欄位清單。
 *
 * - `uniform_number`：唯讀（BR-024）。
 * - `is_uniform_number_visible`：DB 有此欄（`CompanyInfo.ts:249`），2026-09-04 決定**不開放**給
 *   enterprise API／CLI（spec §1「明確不做」）——不是尚未落地，是刻意排除，往後也不會加。
 * - `banner_url`／`photo_1-3`／`video_1-3`：banner 上傳與照片另案（spec §1「明確不做」），
 *   本批未落地，仍是 best-effort 提前擋。
 *
 * 2026-09-04 spec 批 E 移除的舊 placeholder 名稱：`foundation_date`（真實欄位是
 * `establishment_date`）、`representative`（真實欄位是 `representative_name`）、`directors`
 * （名稱本身即真實欄位，現已在 BASIC_FIELDS）、`milestones`／`awards`／`qa`（`qa` 錯名，真實是
 * `qa_items`；三者現由 `company replace <section>` 落地，不再是 CLI 完全不支援的欄位）。
 */
export const FORBIDDEN_FIELDS: ReadonlyArray<string> = [
	'uniform_number',
	'is_uniform_number_visible',
	'banner_url',
	'photo_1',
	'photo_2',
	'photo_3',
	'video_1',
	'video_2',
	'video_3',
];
